import React, { useState } from "react";
import "./addmember.css";
import axiosInstance from "../../../../helpers/axiosInstance";
import useAuth from "../../../../hooks/useAuth";
import { toast } from "react-toastify";
import Loading from "../../../../shared/loading/Loading";
import useRoleOptions from "../../../../hooks/useRoleOptions";
import { Confirm } from "../../../../shared/ui/ui";

function AddMember(props) {
  const { organisation, can } = useAuth();
  // A member created here gets a default password from the server and has
  // no way to know it, so — as the app does — offer the join link straight
  // away. The invite is what lets them set their own.
  const [offerInvite, setOfferInvite] = useState(null);
  const roleOptions = useRoleOptions();
  const [incomplete, setIncomlete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inviteMode, setInviteMode] = useState(false);
  const [selectedMember, setSelectedMember] = useState({});
  const handleOnChange = ({ target }) => {
    const selectedMemberObj = {
      ...selectedMember,
      [target.name]: target.value,
    };
    setSelectedMember(selectedMemberObj);
  };
  const handleSubmit = () => {
    setLoading(true);
    if ((inviteMode && selectedMember?.email && validateEmail()) || (!inviteMode && checkRequired() && validatePhoneNo() && validateEmail())) {
      const selectedMemberObj = {
        id: selectedMember._id,
        email: selectedMember?.email.toString().toLowerCase(),
        ...selectedMember,
      };
      delete selectedMemberObj._id;
      const endpoint = inviteMode ? "/invite" : "/create";
      axiosInstance
        .post(`/api/member/${organisation._id}${endpoint}`, selectedMemberObj)
        .then((response) => {
          setLoading(false);
          toast(inviteMode ? "Invite sent successfully" : "Member added successfully");
          if (inviteMode) props.updateParent();
          else setOfferInvite(selectedMemberObj);
          console.log(response.data);
        })
        .catch((error) => {
          setLoading(false);
          const message =
            error?.response?.data?.message || error?.response?.data?.error || "Unable to submit. Try again!";
          toast(message);
          console.log(error);
        });
    } else {
      setLoading(false);
      setIncomlete(true);
      toast("Fill up all required fields. Try again!");
    }
  };

  const checkRequired = () => {
    return (
      selectedMember?.member_id &&
      selectedMember?.first_name &&
      selectedMember?.last_name &&
      selectedMember?.gender &&
      selectedMember?.email &&
      selectedMember?.role &&
      selectedMember?.phone_no &&
      selectedMember?.active !== null
    );
  };
  const validatePhoneNo = () => {
    return selectedMember?.phone_no?.match(/^\d{10}$/);
  };
  const validateEmail = () => {
    return /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(selectedMember?.email);
  };
  const sendInvite = () => {
    setLoading(true);
    const member = offerInvite;
    axiosInstance
      .post(`/api/member/${organisation._id}/invite`, {
        email: member.email,
        member_id: member.member_id,
        first_name: member.first_name,
        last_name: member.last_name,
        role: member.role || "member",
      })
      .then((response) => {
        setLoading(false);
        const link = response.data?.inviteLink;
        if (link) navigator.clipboard?.writeText(link).catch(() => {});
        toast(link ? "Join link sent and copied to clipboard." : response.data?.message || "Join link sent.");
        setOfferInvite(null);
        props.updateParent();
      })
      .catch((error) => {
        setLoading(false);
        toast(error?.response?.data?.message || "Unable to send the join link. Try again!");
      });
  };

  return (
    <>
      {offerInvite ? (
        <Confirm
          title="Send them the join link?"
          confirmLabel="Send join link"
          busy={loading}
          onCancel={() => {
            setOfferInvite(null);
            props.updateParent();
          }}
          onConfirm={sendInvite}
        >
          {`${offerInvite.first_name || "They"} has been added with a password they do not know. The join link emailed to ${offerInvite.email} lets them set their own.`}
        </Confirm>
      ) : null}
      <section className="addmember">
        <div className="addmember-form">
          <label htmlFor="inviteMode">Action</label>
          <select name="inviteMode" value={inviteMode ? "invite" : "create"} onChange={(e) => setInviteMode(e.target.value === "invite")}> 
            <option value="create">Create Member</option>
            <option value="invite">Send Invite Link</option>
          </select>
        </div>
        <div className="addmember-form">
          <label htmlFor="first_name" className="required">
            Member Id
          </label>
          <input
            type="text"
            className={incomplete && !selectedMember?.member_id ? "input-incomplete" : ""}
            name="member_id"
            onChange={handleOnChange}
            value={selectedMember?.member_id}
            required
          />
        </div>
        <div className="addmember-form">
          <label htmlFor="first_name" className="required">
            First Name
          </label>
          <input
            type="text"
            className={incomplete && !selectedMember?.first_name ? "input-incomplete" : ""}
            name="first_name"
            onChange={handleOnChange}
            value={selectedMember?.first_name}
            required
          />
        </div>
        <div className="addmember-form">
          <label htmlFor="last_name" className="required">
            Last Name
          </label>
          <input
            type="text"
            className={incomplete && !selectedMember?.last_name ? "input-incomplete" : ""}
            name="last_name"
            onChange={handleOnChange}
            value={selectedMember?.last_name}
          />
        </div>
        <div className="addmember-form">
          <label htmlFor="role" className="required">
            Role
          </label>
          <select
            name="role"
            className={incomplete && !selectedMember?.role ? "input-incomplete" : ""}
            onChange={handleOnChange}
            value={selectedMember?.role}
            disabled={!can("members", "add")}
            required
          >
            <option value={null}> </option>
            {roleOptions.map((role) => (
              <option key={role.key} value={role.key}>
                {role.name}
              </option>
            ))}
          </select>
        </div>
        <div className="addmember-form">
          <label htmlFor="active" className="required">
            Status
          </label>
          <select
            name="active"
            onChange={handleOnChange}
            value={selectedMember?.active}
            disabled={!can("members", "add")}
            required
          >
            <option value={null}> </option>
            <option value={true}>Active</option>
            <option value={false}>In-Active</option>
          </select>
        </div>
        <div className="addmember-form">
          <label htmlFor="gender" className="required">
            Gender
          </label>
          <select
            name="gender"
            className={incomplete && !selectedMember?.gender ? "input-incomplete" : ""}
            onChange={handleOnChange}
            value={selectedMember?.gender}
            required
          >
            <option value={null}> </option>
            <option value={"male"}>Male</option>
            <option value={"female"}>Female</option>
          </select>
        </div>
        <div className="addmember-form">
          <label htmlFor="address">Address</label>
          <input type="text" name="address" onChange={handleOnChange} value={selectedMember?.address} />
        </div>
        <div className="addmember-form">
          <label htmlFor="state">State</label>
          <input type="text" name="state" onChange={handleOnChange} value={selectedMember?.state} />
        </div>
        <div className="addmember-form">
          <label htmlFor="city">City</label>
          <input type="text" name="city" onChange={handleOnChange} value={selectedMember?.city} />
        </div>
        <div className="addmember-form">
          <label htmlFor="nationality">Nationality</label>
          <input type="text" name="nationality" onChange={handleOnChange} value={selectedMember?.nationality} />
        </div>
        <div className="addmember-form">
          <label htmlFor="pin_code">Pin Code</label>
          <input type="text" name="pin_code" onChange={handleOnChange} value={selectedMember?.pin_code} />
        </div>
        <div className="addmember-form">
          <label htmlFor="age">Age</label>
          <input type="text" name="age" onChange={handleOnChange} value={selectedMember?.age} />
        </div>
        <div className="addmember-form">
          <label htmlFor="email" className="required">
            Email
          </label>
          <input
            type="text"
            className={incomplete && !validateEmail() ? "input-incomplete" : ""}
            name="email"
            onChange={handleOnChange}
            value={selectedMember?.email}
            disabled={!can("members", "add")}
            required
          />
        </div>
        <div className="addmember-form">
          <label htmlFor="phone_no" className="required">
            Phone No
          </label>
          <input
            className={incomplete && !validatePhoneNo() ? "input-incomplete" : ""}
            type="text"
            name="phone_no"
            onChange={handleOnChange}
            value={selectedMember?.phone_no}
          />
        </div>
        <div className="addmember-form">
          <label htmlFor="age">UPI Id</label>
          <input type="text" name="upi_id" onChange={handleOnChange} value={selectedMember?.upi_id} />
        </div>
      </section>
      <div className="addmember-footer">
        <button className="addmember-formsubmit" onClick={handleSubmit}>
          {loading ? <Loading /> : "Submit"}
        </button>
      </div>
    </>
  );
}

export default AddMember;
